import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Construction, Table2, CalendarDays } from "lucide-react";
import { AppShell } from "@/components/shell/AppShell";
import { ModuleHeader, FeedChips, EmptyState, Loading, Panel, PillSelect, Toolbar, Badge } from "@/components/kit";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/hooks/useModules";
import api from "@/lib/api";

const monthLabel = (m) => new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleString("en", { month: "long", year: "numeric" });

const DataPreview = ({ slug, tenantId, accent }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [tab, setTab] = useState("");
  useEffect(() => {
    api.get(`/modules/${slug}/data?tenant_id=${tenantId}&month=${month}&tab=${encodeURIComponent(tab)}`)
      .then((r) => { setData(r.data); setMonth(r.data.month); setTab(r.data.tab); }).catch(() => setData(null));
  }, [slug, tenantId, month, tab]);
  if (!data) return <div className="mk-skel h-64" />;
  return (
    <>
      <Toolbar testid="module-preview-toolbar">
        <PillSelect testid="module-preview-month" icon={CalendarDays} value={month} onChange={(e) => setMonth(e.target.value)}>
          {[...data.months].sort().reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </PillSelect>
        <PillSelect testid="module-preview-tab" icon={Table2} value={tab} onChange={(e) => setTab(e.target.value)}>
          {data.tabs.map((t) => <option key={t} value={t}>{t}</option>)}
        </PillSelect>
        <Badge tone="info" testid="module-preview-count">{data.total.toLocaleString()} rows{data.truncated ? ` · showing first ${data.rows.length}` : ""}</Badge>
      </Toolbar>
      <Panel title={`Data preview · ${tab} · ${month}`} testid="module-preview-table" className="!p-0 overflow-hidden">
        <div className="max-h-[560px] overflow-auto">
          <table className="mk-table">
            <thead><tr>{data.columns.map((c, i) => <th key={i} className={i === 0 ? "pl-5" : ""}>{c}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci} className={`whitespace-nowrap ${ci === 0 ? "pl-5 font-semibold text-thstrong" : ""} ${/^-?[\d,.]+%?$/.test(String(c)) ? "font-mono-hud text-right" : ""}`} style={ci === 0 ? { color: accent } : undefined}>{String(c ?? "")}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

export default function ModulePage() {
  const { slug } = useParams();
  const { tenantId, tenant } = useTenant() || {};
  const { user } = useAuth();
  const { modules } = useModules();
  const [ledger, setLedger] = useState(null);
  const mod = (modules || []).find((m) => m.slug === slug);
  const meta = { name: mod?.name || slug, sub: mod?.desc || "", accent: mod?.color || "#14B8A6", builtin: mod?.builtin };
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const load = useCallback(() => {
    if (!tenantId) { setLedger([]); return; }
    api.get(`/uploads/${slug}/ledger?tenant_id=${tenantId}`).then((r) => setLedger(r.data)).catch(() => setLedger([]));
  }, [slug, tenantId]);
  useEffect(() => { setLedger(null); load(); }, [load]);

  const months = (ledger || []).map((l) => l.month);
  const last = (ledger || []).slice(-1)[0];
  const hasData = months.length > 0;

  return (
    <AppShell wide>
      <ModuleHeader testid="module-title" title={meta.name} subtitle={meta.builtin && slug === "l2" ? "Escalations & Backlog" : "Data Explorer"} accent={meta.accent}
        context={`${tenant?.name || "No client"} · Workspace data · monthly uploads`} />
      {ledger === null || !modules ? <Loading label="Checking uploads…" /> : (
        <>
          <FeedChips testid="module-coverage" months={months}
            coverage={last ? Object.fromEntries(Object.entries(last.rows_per_tab || {}).map(([t, n]) => [t, last.date_range?.max || `${Number(n).toLocaleString()} rows`])) : {}}
            hint={hasData ? (meta.builtin ? "Data explorer shown until the bespoke dashboard ships" : "Every uploaded month is available in the preview below") : "Upload a month to see its data here"} />
          {hasData ? <DataPreview slug={slug} tenantId={tenantId} accent={meta.accent} /> : (
            <EmptyState testid="module-awaiting" icon={Construction} title="No uploads yet" isAdmin={isAdmin}
              body={`${meta.name} will show a live preview of every uploaded month as soon as the first file lands${meta.builtin ? ", until its bespoke dashboard ships" : ""}. Define the expected tabs and columns in Settings → Data Uploads → Edit format.`}
              action={{ to: "/settings?section=uploads", label: "Open Data Uploads" }} />
          )}
        </>
      )}
    </AppShell>
  );
}
